import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ProgressBarModule } from 'primeng/progressbar';
import { TableModule } from 'primeng/table';
import { CheckboxModule } from 'primeng/checkbox';
import { TagModule } from 'primeng/tag';
import { forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { NotificationItem } from '../../../models/notification-item.model';

import { TranslateService } from '../../../services/translate.service';
import { NotificationDialogService } from './notification-dialog.service';



@Component({
	selector: 'app-notification-dialog',
	standalone: true,
	imports: [
		CommonModule,
		FormsModule,
		DialogModule,
		ProgressBarModule,
		TableModule,
		CheckboxModule,
		TagModule
	],
	templateUrl: './notification-dialog.component.html',
	styleUrls: ['./notification-dialog.component.scss'],
	providers: [DatePipe]
})

export class NotificationDialogComponent implements OnChanges {
	@Input() fileRecId: number | null = null;
	@Input() visible: boolean = false;
	@Input() isAllowedToChange: boolean = true;
	@Output() visibleChange = new EventEmitter<boolean>();
	@Output() notificationSummaryChange = new EventEmitter<NotificationSummaryChange>();

	notificationsData: NotificationItem[] = [];
	isNotificationsLoading = false;
	notificationUpdatesInProgress: { [recId: number]: boolean } = {};

	constructor(
		private notificationDialogService: NotificationDialogService,
		public translateServ: TranslateService
	) { }

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['visible'] && this.visible && this.fileRecId) {
			this.loadNotifications();
		}
	}

	loadNotifications(): void {
		if (!this.fileRecId) return;
		this.isNotificationsLoading = true;
		this.notificationsData = [];
		this.notificationDialogService.getFileNotifications(this.fileRecId).subscribe({
			next: (notifications: NotificationItem[]) => {
				this.notificationsData = notifications;
				this.emitNotificationSummaryChange();
				this.isNotificationsLoading = false;
			},
			error: () => {
				this.isNotificationsLoading = false;
			}
		});
	}

	close(): void {
		this.visible = false;
		this.visibleChange.emit(false);
	}

	onNotificationCheckboxChange(row: NotificationItem, checked: boolean): void {
		if (!this.isAllowedToChange || !this.fileRecId || this.notificationUpdatesInProgress[row.RecID]) return;

		const nextValue: 0 | 1 = checked ? 1 : 0;
		this.notificationUpdatesInProgress[row.RecID] = true;

		this.notificationDialogService.updateFileNotification(row.RecID, nextValue).subscribe({
			next: () => {
				row.IsNotificationResolved = nextValue;
				this.emitNotificationSummaryChange();
				this.notificationUpdatesInProgress[row.RecID] = false;
			},
			error: (error) => {
				console.error(`Error updating notification status for FileRecID ${this.fileRecId}:`, error);
				this.notificationUpdatesInProgress[row.RecID] = false;
			}
		});
	}

	onResolveAllNotificationsChange(checked: boolean): void {
		if (!this.isAllowedToChange || !this.fileRecId || this.isAnyNotificationUpdateInProgress()) return;

		const nextValue: 0 | 1 = checked ? 1 : 0;
		const notificationsToUpdate = this.notificationsData.filter(row => row.IsNotificationResolved !== nextValue);
		if (notificationsToUpdate.length === 0) return;

		notificationsToUpdate.forEach(row => {
			this.notificationUpdatesInProgress[row.RecID] = true;
		});

		forkJoin(notificationsToUpdate.map(row =>
			this.notificationDialogService.updateFileNotification(row.RecID, nextValue).pipe(
				map(() => ({ row, updated: true })),
				catchError(error => {
					console.error(`Error updating notification status for FileRecID ${this.fileRecId}:`, error);
					return of({ row, updated: false });
				})
			)
		)).subscribe(results => {
			results.forEach(result => {
				if (result.updated) {
					result.row.IsNotificationResolved = nextValue;
				}

				this.notificationUpdatesInProgress[result.row.RecID] = false;
			});

			this.emitNotificationSummaryChange();
		});
	}

	isNotificationUpdateInProgress(row: NotificationItem): boolean {
		return this.notificationUpdatesInProgress[row.RecID] === true;
	}

	isAnyNotificationUpdateInProgress(): boolean {
		return Object.values(this.notificationUpdatesInProgress).some(isInProgress => isInProgress);
	}

	areAllNotificationsResolved(): boolean {
		return this.notificationsData.length > 0 && this.notificationsData.every(row => row.IsNotificationResolved === 1);
	}

	getNotificationDetailSeverity(color: string | null): NotificationSeverity {
		return normalizeNotificationSeverity(color);
	}

	private emitNotificationSummaryChange(): void {
		if (!this.fileRecId) return;

		const tag = buildNotificationTag(this.notificationsData);
		this.notificationSummaryChange.emit({
			fileRecId: this.fileRecId,
			NotifCount: Number(tag.value) || 0,
			NotifColor: Number(tag.value) > 0 ? tag.severity : null,
			tag
		});
	}
}


export type NotificationSeverity = 'info' | 'success' | 'help' |'warn' | 'danger' | 'secondary' | 'contrast';

export interface NotificationTagInfo {
	value: string;
	severity: NotificationSeverity;
	tooltip: string;
}

export interface NotificationSummaryChange {
	fileRecId: number;
	NotifCount: number;
	NotifColor: NotificationSeverity | null;
	tag: NotificationTagInfo;
}

export interface NotificationTagOptions {
	emptyTooltip?: string;
	countTooltipSuffix?: string;
	fallbackSeverity?: NotificationSeverity;
}

export interface NotificationTagSource {
	NotifCount?: number | string | null;
	NotifColor?: unknown;
	UnresolvedNotifCount?: number | string | null;
	UnresolvedNotifColor?: unknown;
	count?: number | string | null;
	color?: unknown;
	notifications?: NotificationItem[] | null;
	Notifications?: NotificationItem[] | null;
}

export function normalizeNotificationSeverity(color: unknown, fallbackSeverity: NotificationSeverity = 'success'): NotificationSeverity {
	if (typeof color !== 'string') {
		return fallbackSeverity;
	}

	const normalizedColor = color.trim().toLowerCase();
	if (normalizedColor === 'warning') {
		return 'warn';
	}


	if (['info', 'success', 'help', 'warn', 'danger', 'secondary', 'contrast'].includes(normalizedColor)) {
		return normalizedColor as NotificationSeverity;
	}

	return fallbackSeverity;
}

export function buildNotificationTag(
	notification: NotificationTagSource | NotificationItem[],
	options: NotificationTagOptions = {}
): NotificationTagInfo {
	const notificationItems = Array.isArray(notification)
		? notification
		: notification.notifications ?? notification.Notifications;
	const unresolvedNotifications = notificationItems?.filter(item => item.IsNotificationResolved !== 1);
	const strongestUnresolvedColor = getStrongestNotificationColor(unresolvedNotifications);
	const rawCount = unresolvedNotifications
		? unresolvedNotifications.length
		: Array.isArray(notification)
			? 0
			: notification.UnresolvedNotifCount ?? notification.NotifCount ?? notification.count ?? 0;
	const notificationCount = Number(rawCount) || 0;
	const displayCount = notificationCount > 0 ? notificationCount : 0;
	const countTooltipSuffix = options.countTooltipSuffix ?? 'notifications';
	const rawColor = strongestUnresolvedColor ?? (Array.isArray(notification)
		? null
		: notification.UnresolvedNotifColor ?? notification.NotifColor ?? notification.color);

	return {
		value: displayCount.toString(),
		severity: displayCount > 0 ? normalizeNotificationSeverity(rawColor, options.fallbackSeverity) : (options.fallbackSeverity ?? 'success'),
		tooltip: displayCount > 0 ? `${displayCount} ${countTooltipSuffix}` : (options.emptyTooltip ?? 'All Clear')
	};
}

function getStrongestNotificationColor(notifications: NotificationItem[] | undefined): unknown {
	if (!notifications?.length) {
		return null;
	}

	const severityRank: Record<NotificationSeverity, number> = {
		contrast : 7,
		danger: 6,
		warn: 5,
		help : 4,
		info: 3,
		secondary: 2,
		success: 1
	};

	return notifications
		.map(item => item.ColorNotification)
		.sort((left, right) => severityRank[normalizeNotificationSeverity(right)] - severityRank[normalizeNotificationSeverity(left)])[0];
}
